import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { CardFaceData } from '../../../../global-types';
import { CanvasDataTypes } from '../../../../utils';
import { applyOverlayTextStyles } from '../../utils/apply-overlay-text-styles';
import { positionJsxOverFabric } from '../../utils/position-jsx-over-canvas';
import { toggleOverlayVisibility } from '../../utils/toggle-overlay-visibility';
import styles from './overlay-textarea.module.scss';
import { useFeatureFlags, useIsSignAndSend } from '../../../../hooks';
import { fillPhotoZoneWithOverlayText } from '../../utils/fill-photo-text-zone-with-overlay-text';
import { setIsTextDrawerOpen, useAppContext } from '../../../../context/app-context';
import { validatePhotoTextZoneHasContent } from '../../utils/validate-photo-text-zone-has-content';

type OverlayTextareaProps = {
  canvasRef: React.RefObject<fabric.Canvas>;
  cardFace: CardFaceData;
};

type SyncOverlayTextAreaProps = {
  canvas: fabric.Canvas;
  fabricTextbox: fabric.Textbox;
};

type onShowOverlayTextareaProps = {
  fabricTextbox: fabric.Textbox;
};

type OnMinTextLimitReachedProps = {
  fabricTextbox: fabric.Textbox;
};

export type OverlayTextHandles = {
  onShowOverlayTextarea: (props: onShowOverlayTextareaProps) => {
    overlayTextarea?: HTMLTextAreaElement;
    fabricTextbox?: fabric.Textbox;
  };
  onTextareaBlur: () => {
    overlayTextarea?: HTMLTextAreaElement;
    fabricTextbox?: fabric.Textbox;
  };
  syncOverlayTextArea: (props: SyncOverlayTextAreaProps) => void;
  focus: () => void;
  onMinTextLimitReached: ({ fabricTextbox }: OnMinTextLimitReachedProps) => void;
  isOverlayTextareaVisible: () => boolean;
};

export const OverlayTextarea = forwardRef<OverlayTextHandles, OverlayTextareaProps>(({ canvasRef, cardFace }, ref) => {
  const [activeTextareaId, setActiveTextareaId] = useState<fabric.Textbox | null>(null);
  const [textareaContent, setTextareaContent] = useState<string>('default text');
  const [normalizedZoomLevel, setNormalizedZoomLevel] = useState<number | null>(null);
  const isSignAndSendCard = useIsSignAndSend();
  const overlayTextareaRef = React.useRef<HTMLTextAreaElement>(null);
  const fabricTexboxRef = React.useRef<fabric.Textbox | null>(null);
  const parentPhotoTextZoneRef = React.useRef<fabric.Group | null>(null);
  const { SAS_DYNAMIC_BUTTONS } = useFeatureFlags();
  const { appDispatch, appState } = useAppContext();
  const { isDrawerInteracting } = appState;

  // Updates the overlay textarea styles, position, and content based on the fabric textbox
  const syncOverlayTextArea = ({ canvas, fabricTextbox }: SyncOverlayTextAreaProps) => {
    const overlayTextarea = overlayTextareaRef.current;
    // Get canvas zoom level
    let canvasZoom = canvas.getZoom();
    if (!canvasZoom || !overlayTextarea) {
      throw new Error('Canvas zoom or overlayTextarea missing when syncing overlay styles');
    }

    // For landscape cards the zoom doubles after normalizing. So we need to ensure the zoom
    // level remains consistent on to be in sync with the canvas.
    if (canvasZoom !== normalizedZoomLevel && normalizedZoomLevel) {
      canvasZoom = normalizedZoomLevel;
    }

    // Get text from fabric textbox
    const text = fabricTextbox.text ?? textareaContent;

    // Set text to the current value of the textarea
    setTextareaContent(text);

    // send the phototextzone inteasd of fabricTextbox for sign and send
    // Set overlay position
    positionJsxOverFabric({
      fabricObject: parentPhotoTextZoneRef.current || fabricTextbox,
      element: overlayTextarea,
      canvasZoom,
    });

    // Apply text styles to text areas
    applyOverlayTextStyles({
      overlayTextarea,
      fabricTextbox,
      canvasZoom,
      isPhotoTextZone: parentPhotoTextZoneRef.current ? true : false,
    });

    // Return the overlay textarea and fabric textbox for further use (mainly for testing)
    return { fabricTextbox, overlayTextarea, parentPhotoTextZoneRef };
  };

  // Callback to trigger when text limits have been reached and recent changes needto be reversed
  const onMinTextLimitReached = ({ fabricTextbox }: OnMinTextLimitReachedProps) => {
    // External function to handle when the minimum text limit is reached
    if (!fabricTextbox) {
      throw new Error('err_canvas_or_textbox_not_found');
    }
    // If the textbox is not defined, return empty string to prevent errors (shouldn't be possible)
    const textareaContent = fabricTextbox.text ?? '';
    setTextareaContent(textareaContent);

    // Return the current textarea content for use and testing
    return textareaContent;
  };

  // Triggers when the textbox is clicked.  Should be tied to the canvas event
  const onShowOverlayTextarea = ({ fabricTextbox }: onShowOverlayTextareaProps) => {
    const canvas = canvasRef?.current;
    const overlayTextarea = overlayTextareaRef?.current;
    // If the canvas, textboxID, or overlayTextarea are not defined, return object with nulls
    if (!canvas || !overlayTextarea || !fabricTextbox?.data?.ID) {
      throw new Error('Canvas, overlay textarea, or fabric textbox ID is not defined');
    }

    // Used to set zoom level to fix text size issues on landscape cards
    if (!normalizedZoomLevel) {
      setNormalizedZoomLevel(canvas.getZoom());
    }

    fabricTexboxRef.current = fabricTextbox;

    parentPhotoTextZoneRef.current = canvas
      .getObjects()
      .find(
        (obj) => obj.data?.type === CanvasDataTypes.PhotoTextZone && obj.data.ID === fabricTextbox.data.ID,
      ) as fabric.Group;

    // Set text alignment based on data type.
    const textboxType: CanvasDataTypes = fabricTextbox.data.type;
    overlayTextarea.style.alignContent = textboxType === CanvasDataTypes.EditableText ? 'center' : 'start';

    // Exit editing mode in textbox and focus on textarea
    fabricTextbox.exitEditing();
    canvas.renderAll();
    // Wait for the next paint after renderAll before focusing
    requestAnimationFrame(() => {
      overlayTextareaRef.current?.focus();
    });

    // Syncs the overlay textarea with the fabric textbox
    syncOverlayTextArea({
      canvas,
      fabricTextbox,
    });

    // Show overlay textarea and hide fabric textbox
    toggleOverlayVisibility({
      shouldShowOverlay: true,
      fabricObject: fabricTextbox,
      overlayElement: overlayTextarea,
    });

    // Set textarea contents and activeID to state
    setTextareaContent(fabricTextbox.text ?? '');
    // Don't open thetext drawer if editing an editable text box
    setActiveTextareaId(fabricTextbox.data.ID);
    if (parentPhotoTextZoneRef.current) {
      setIsTextDrawerOpen(appDispatch);
    } else {
      canvas.discardActiveObject();
    }

    return { overlayTextarea: overlayTextareaRef.current || null, fabricTextbox };
  };

  // Triggers when the textarea overlay is blurred
  const onTextareaBlur = () => {
    // Ensure dependencies are all here
    const canvasObjects = canvasRef?.current?.getObjects();
    const activeTextbox = activeTextareaId;
    const overlayTextarea = overlayTextareaRef?.current;
    if (!canvasRef || activeTextareaId === null || !canvasObjects || !activeTextbox || !overlayTextarea) {
      return {
        overlayTextarea: overlayTextareaRef.current || undefined,
        fabricTextbox: fabricTexboxRef.current || undefined,
      };
    }

    // Reset these to default to ensure logic doens't bleed into next selected textbox
    setActiveTextareaId(null);
    setNormalizedZoomLevel(null);

    // Find the fabric textbox with the same ID as the activeTextbox
    const fabricTextbox = fabricTexboxRef.current;
    // If the fabric textbox is not found, return
    if (!fabricTextbox) {
      throw new Error('No fabric textbox found with the active ID');
    }

    // Set textbox to use textarea contents
    // fabricTextbox.text = textareaContent;

    if (isSignAndSendCard) {
      validatePhotoTextZoneHasContent({
        canvas: canvasRef.current as fabric.Canvas,
        textBox: fabricTextbox,
        photoTextZone: parentPhotoTextZoneRef.current as fabric.Group,
        SAS_DYNAMIC_BUTTONS,
      });
    }

    toggleOverlayVisibility({ shouldShowOverlay: false, fabricObject: fabricTextbox, overlayElement: overlayTextarea });

    fabricTextbox.fire('editing:exited', { target: fabricTextbox });

    canvasRef?.current?.renderAll();
    // Check if blur was caused by drawer interaction

    if (isDrawerInteracting) {
      return {
        overlayTextarea: overlayTextareaRef.current || undefined,
        fabricTextbox: fabricTexboxRef.current || undefined,
      };
    }

    // Wait for the next paint after renderAll before focusing
    requestAnimationFrame(() => {
      // For template textareas, we want to completely delete the textarea.  Otherwise, select the textbox.
      if (fabricTextbox.data?.type !== CanvasDataTypes.EditableText) {
        canvasRef.current?.setActiveObject(fabricTextbox);
      }
    });

    // trigger the changed event to check bound of the textbox within the photo text zone
    if (isSignAndSendCard) {
      fabricTexboxRef.current?.fire('changed', {
        target: fabricTexboxRef.current,
      });
    }

    // Return the manipulated elements. Useful for testing purposes.
    return { overlayTextarea, fabricTextbox };
  };

  // Exposes the above methods to the parent component
  useImperativeHandle(ref, () => ({
    onShowOverlayTextarea,
    onTextareaBlur,
    syncOverlayTextArea,
    focus: () => {
      overlayTextareaRef.current?.focus();
    },
    onMinTextLimitReached,
    isOverlayTextareaVisible: () => {
      const overlayTextarea = overlayTextareaRef.current;
      return overlayTextarea ? overlayTextarea.style.display !== 'none' : false;
    },
  }));

  const onTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // Get all dependencie
    const fabricTextbox = fabricTexboxRef.current;
    const overlayTextarea = overlayTextareaRef.current;
    const canvas = canvasRef.current;
    const text = e.target.value;
    if (!fabricTextbox || !overlayTextarea || !canvas) {
      throw new Error('err_canvas_or_textbox_not_found');
    }
    // Set fabric textbox value to be new textarea
    fabricTextbox.set({ text });

    canvas.renderAll();

    // Get cursor position on textarea
    const cursorPosition = overlayTextarea.selectionStart;
    fabricTextbox.setSelectionEnd(cursorPosition);
    // Set the textarea content to the new value
    setTextareaContent(e.target.value);

    // If sign and send, fill the photo text zone with the overlay text and hide the buttons
    if (isSignAndSendCard) {
      fillPhotoZoneWithOverlayText({
        canvas: canvasRef.current as fabric.Canvas,
        textBox: fabricTextbox,
        photoTextZone: parentPhotoTextZoneRef.current as fabric.Group,
        text,
        SAS_DYNAMIC_BUTTONS,
      });
    }

    // Fire textarea change event, which triggers our resize logic
    canvas.fire('text:changed', {
      target: fabricTextbox,
    });

    canvas.renderAll();

    e.preventDefault();
    e.stopPropagation();
  };

  const textareaId = cardFace?.type ? `overlay-textarea-${cardFace.type}` : 'overlay-textarea';
  return (
    <>
      <textarea
        id={textareaId}
        data-testid={textareaId}
        className={styles['overlay-textarea']}
        value={textareaContent}
        rows={3}
        wrap="off"
        spellCheck="true"
        autoCorrect="on"
        ref={overlayTextareaRef}
        onInput={onTextAreaChange}
        onBlur={onTextareaBlur}
        // This style is defined inline because it is overwritten by js above.
        style={{ display: 'none' }}
      />
    </>
  );
});
